import { useState, useRef } from 'react';
import { toast } from 'sonner';
import { API_BASE, apiGet, fetchWithRetry } from '../appUtils';
import { getDemoReplies } from '../demoProfiles';
import { isDemoVisit } from '../AccessGate';

// ---- Types ----

export type LiveCirclePost = {
  id: number;
  postType: string;
  text: string;
  journey: string;
  createdAt: string;
  handle: string;
  avatarEmoji: string;
  isMod: boolean;
  replyCount: number;
  mediaUrl?: string | null;
  gifUrl?: string | null;
  mediaAlt?: string | null;
};

export type CircleReply = {
  id: number;
  postId: number;
  text: string;
  createdAt: string;
  handle: string;
  avatarEmoji: string;
  isMod: boolean;
};

export interface SafetyWord { id: number; word: string }
export interface SafetyMember { id: number; mutedHandle?: string; blockedHandle?: string }

export const CIRCLE_MEDIA_CONSENT_KEY = 'careweave_circle_media_consent';

// ---- Hook ----

export function useCircle(authToken: string | null) {
  // Circle identity / profile state
  const [circleProfile, setCircleProfile] = useState<{ nickname: string; bio: string | null } | null | undefined>(undefined);
  const [circleDisclosure, setCircleDisclosure] = useState<{ ageBracket: string | null; scdType: string | null; customTags: string[] } | null>(null);
  const [circleSetupModal, setCircleSetupModal] = useState(false);
  const [showCircleWelcome, setShowCircleWelcome] = useState(false);
  const [circleSetupNickname, setCircleSetupNickname] = useState('');
  const [circleSetupBio, setCircleSetupBio] = useState('');
  const [circleSetupNicknameError, setCircleSetupNicknameError] = useState('');
  const [circleSetupBioError, setCircleSetupBioError] = useState('');
  const [circleSetupSaving, setCircleSetupSaving] = useState(false);
  const [circleEditNickname, setCircleEditNickname] = useState('');
  const [circleEditBio, setCircleEditBio] = useState('');
  const [circleEditNicknameError, setCircleEditNicknameError] = useState('');
  const [circleEditBioError, setCircleEditBioError] = useState('');
  const [circleEditSaving, setCircleEditSaving] = useState(false);
  const [circleEditSaved, setCircleEditSaved] = useState(false);
  const [circleDisclosureEditAgeBracket, setCircleDisclosureEditAgeBracket] = useState<string | null>(null);
  const [circleDisclosureEditScdType, setCircleDisclosureEditScdType] = useState<string | null>(null);
  const [circleDisclosureEditScdOther, setCircleDisclosureEditScdOther] = useState('');
  const [circleDisclosureEditScdShowOther, setCircleDisclosureEditScdShowOther] = useState(false);
  const [circleDisclosureEditTags, setCircleDisclosureEditTags] = useState<string[]>([]);
  const [circleDisclosureEditTagInput, setCircleDisclosureEditTagInput] = useState('');
  const [circleDisclosureEditTagError, setCircleDisclosureEditTagError] = useState('');
  const [circleDisclosureEditSaving, setCircleDisclosureEditSaving] = useState(false);
  const [circleDisclosureEditSaved, setCircleDisclosureEditSaved] = useState(false);

  // Circle thread state
  const [circleLivePosts, setCircleLivePosts] = useState<LiveCirclePost[]>([]);
  const circleAbortRef = useRef<AbortController | null>(null);
  const [circleThreadPost, setCircleThreadPost] = useState<{ id: number; handle: string; avatarEmoji: string; text: string; type: string; journey: string; time: string } | null>(null);
  const [circleThreadReplies, setCircleThreadReplies] = useState<CircleReply[]>([]);
  const [circleThreadLoading, setCircleThreadLoading] = useState(false);
  const [circleThreadReplyText, setCircleThreadReplyText] = useState('');
  const [circleThreadReplyLoading, setCircleThreadReplyLoading] = useState(false);
  const [circleThreadReplyError, setCircleThreadReplyError] = useState<string | null>(null);
  const [circleThreadReplyCounts, setCircleThreadReplyCounts] = useState<Record<number, number>>({});
  const [circleThreadRemovedReplies, setCircleThreadRemovedReplies] = useState<Set<number>>(new Set());
  const [circleThreadReportedReplies, setCircleThreadReportedReplies] = useState<Set<number>>(new Set());

  // Peer Connect state
  const [peerFilter, setPeerFilter] = useState('all');
  const [peerAgeFilter, setPeerAgeFilter] = useState('all');
  const [peerScdFilter, setPeerScdFilter] = useState('all');
  const [peerView, setPeerView] = useState<'feed' | 'topics' | 'circle-profile'>('feed');
  const [peerSelectedTopic, setPeerSelectedTopic] = useState<string | null>(null);
  const [peerReactions, setPeerReactions] = useState<Record<number, Record<string, number>>>({ 1: { '🫂': 34, '❤️': 12, '🔥': 8 }, 2: { '🫂': 89, '❤️': 61, '🔥': 3 }, 3: { '🫂': 107, '❤️': 88, '🔥': 41 }, 4: { '🫂': 44, '❤️': 19 }, 5: { '🫂': 22, '❤️': 8, '🔥': 5 }, 6: { '🫂': 61, '❤️': 44, '🔥': 2 } });
  const [peerReacted, setPeerReacted] = useState<Record<string, boolean>>({});
  const [peerEmojiPickerPost, setPeerEmojiPickerPost] = useState<number | null>(null);
  const [peerPostModal, setPeerPostModal] = useState(false);
  const [peerPostType, setPeerPostType] = useState<'tip' | 'solidarity' | 'story' | 'question'>('tip');
  const [peerPostText, setPeerPostText] = useState('');
  const [peerRemovedPosts, setPeerRemovedPosts] = useState<Set<number>>(new Set());
  const [peerRemovalModal, setPeerRemovalModal] = useState<{ postId: number; handle: string } | null>(null);
  const [peerRemovalReason, setPeerRemovalReason] = useState<'HATE_SLUR' | 'STRUCTURAL_HARM_SLUR' | 'PRIVACY_RISK' | null>(null);
  const [peerRemovalConfirmed, setPeerRemovalConfirmed] = useState(false);
  const [peerPostError, setPeerPostError] = useState<string | null>(null);
  const [peerPostNudge, setPeerPostNudge] = useState<string | null>(null);
  const [peerPostLoading, setPeerPostLoading] = useState(false);
  const [showCircleEmojiPicker, setShowCircleEmojiPicker] = useState(false);
  const [circleEmojiDraft, setCircleEmojiDraft] = useState('');
  const [peerRevealedPosts, setPeerRevealedPosts] = useState<Set<number>>(new Set());

  // Circle safety / self-moderation state
  const [safetyMutedWords, setSafetyMutedWords] = useState<SafetyWord[]>([]);
  const [safetyMutedMembers, setSafetyMutedMembers] = useState<SafetyMember[]>([]);
  const [safetyBlockedMembers, setSafetyBlockedMembers] = useState<SafetyMember[]>([]);
  const [safetyLoaded, setSafetyLoaded] = useState(false);
  const [safetyNewWord, setSafetyNewWord] = useState('');
  const [safetyWordError, setSafetyWordError] = useState('');

  // Circle media consent state
  const [circleMediaConsentModal, setCircleMediaConsentModal] = useState(false);
  const [circleMediaConsentPendingAction, setCircleMediaConsentPendingAction] = useState<'image' | 'gif' | null>(null);
  const [peerPostImage, setPeerPostImage] = useState<string | null>(null);
  const [peerPostImageName, setPeerPostImageName] = useState<string>('');
  const [peerPostAltText, setPeerPostAltText] = useState('');
  const [peerPostGif, setPeerPostGif] = useState<{ url: string; alt: string } | null>(null);
  const [peerPostImageUploading, setPeerPostImageUploading] = useState(false);
  const [peerPostImageError, setPeerPostImageError] = useState('');
  const [peerPostImageContentNote, setPeerPostImageContentNote] = useState<string | null>(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifCategories, setGifCategories] = useState<{ id: number; slug: string; label: string; enabled: boolean }[]>([]);
  const [gifItems, setGifItems] = useState<{ id: string; url: string; alt: string; category: string }[]>([]);
  const [gifFilter, setGifFilter] = useState('all');
  const [adminGifCategories, setAdminGifCategories] = useState<{ id: number; slug: string; label: string; enabled: boolean }[]>([]);
  const [adminModerationQueue, setAdminModerationQueue] = useState<{ id: number; violation_type: string; moderation_reason: string | null; status: string; created_at: string }[]>([]);

  // ---- Functions ----

  const loadSafetySettings = async () => {
    if (!authToken) return;
    try {
      const data = await apiGet<{ mutedWords: SafetyWord[]; mutedMembers: SafetyMember[]; blockedMembers: SafetyMember[] }>('/circle/safety', authToken);
      if (data) {
        setSafetyMutedWords(data.mutedWords);
        setSafetyMutedMembers(data.mutedMembers);
        setSafetyBlockedMembers(data.blockedMembers);
      }
    } catch { /* error handled silently */ }
    setSafetyLoaded(true);
  };

  const addMutedWord = async (word: string) => {
    if (!authToken) return;
    const trimmed = word.trim().toLowerCase();
    if (!trimmed) return;
    setSafetyWordError('');
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` };
      const res = await fetch(`${API_BASE}/circle/safety/muted-words`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify({ word: trimmed }) });
      if (res.ok) {
        const inserted = await res.json() as SafetyWord;
        setSafetyMutedWords(prev => [...prev, inserted]);
        setSafetyNewWord('');
      } else if (res.status === 409) {
        setSafetyWordError('Already in your muted list');
      }
    } catch { /* error handled silently */ }
  };

  const removeMutedWord = async (id: number) => {
    if (!authToken) return;
    const prev = safetyMutedWords;
    setSafetyMutedWords(words => words.filter(w => w.id !== id));
    try {
      const res = await fetch(`${API_BASE}/circle/safety/muted-words/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${authToken}` }, credentials: 'include' });
      if (!res.ok) setSafetyMutedWords(prev);
    } catch { setSafetyMutedWords(prev); }
  };

  const muteMember = async (handle: string) => {
    if (!authToken) return;
    const trimmed = handle.trim().toLowerCase();
    if (safetyMutedMembers.some(m => m.mutedHandle === trimmed)) return;
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` };
      const res = await fetch(`${API_BASE}/circle/safety/muted-members`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify({ handle: trimmed }) });
      if (res.ok) {
        const inserted = await res.json() as SafetyMember;
        setSafetyMutedMembers(prev => [...prev, inserted]);
      }
    } catch { /* error handled silently */ }
  };

  const unmuteMember = async (id: number) => {
    if (!authToken) return;
    const prev = safetyMutedMembers;
    setSafetyMutedMembers(members => members.filter(m => m.id !== id));
    try {
      const res = await fetch(`${API_BASE}/circle/safety/muted-members/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${authToken}` }, credentials: 'include' });
      if (!res.ok) setSafetyMutedMembers(prev);
    } catch { setSafetyMutedMembers(prev); }
  };

  const blockMember = async (handle: string) => {
    if (!authToken) return;
    const trimmed = handle.trim().toLowerCase();
    if (safetyBlockedMembers.some(m => m.blockedHandle === trimmed)) return;
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` };
      const res = await fetch(`${API_BASE}/circle/safety/blocked-members`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify({ handle: trimmed }) });
      if (res.ok) {
        const inserted = await res.json() as SafetyMember;
        setSafetyBlockedMembers(prev => [...prev, inserted]);
        setSafetyMutedMembers(prev => prev.filter(m => m.mutedHandle !== trimmed));
      }
    } catch { /* error handled silently */ }
  };

  const unblockMember = async (id: number) => {
    if (!authToken) return;
    const prev = safetyBlockedMembers;
    setSafetyBlockedMembers(members => members.filter(m => m.id !== id));
    try {
      const res = await fetch(`${API_BASE}/circle/safety/blocked-members/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${authToken}` }, credentials: 'include' });
      if (!res.ok) setSafetyBlockedMembers(prev);
    } catch { setSafetyBlockedMembers(prev); }
  };

  async function loadCirclePosts() {
    if (!authToken) return;
    circleAbortRef.current?.abort();
    const controller = new AbortController();
    circleAbortRef.current = controller;
    try {
      const res = await fetchWithRetry(`${API_BASE}/circle/posts`, {
        headers: { Authorization: `Bearer ${authToken}` },
        credentials: 'include',
        signal: controller.signal,
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setCircleLivePosts(data as LiveCirclePost[]);
          const counts: Record<number, number> = {};
          for (const p of data) { counts[p.id] = p.replyCount ?? 0; }
          setCircleThreadReplyCounts(counts);
        }
      }
    } catch (e) { if (!(e instanceof DOMException && e.name === 'AbortError')) toast.error("Couldn't load community posts. Try again in a moment."); }
  }

  async function openCircleThread(post: { id: number; handle: string; avatarEmoji: string; text: string; type: string; journey: string; time: string }) {
    setCircleThreadPost(post);
    setCircleThreadReplies([]);
    setCircleThreadReplyText('');
    setCircleThreadReplyError(null);
    setCircleThreadRemovedReplies(new Set());
    setCircleThreadReportedReplies(new Set());
    if (post.id < 0) { setCircleThreadReplies(getDemoReplies(post.id)); return; }
    if (!authToken) return;
    setCircleThreadLoading(true);
    try {
      const res = await fetchWithRetry(`${API_BASE}/circle/posts/${post.id}/replies`, {
        headers: { Authorization: `Bearer ${authToken}` },
        credentials: 'include',
      });
      if (res.ok) { const data = await res.json(); if (Array.isArray(data)) setCircleThreadReplies(data); }
    } catch { /* thread load failed — non-critical */ }
    setCircleThreadLoading(false);
  }

  async function submitCircleReply() {
    if (!circleThreadPost || !circleThreadReplyText.trim()) return;
    // Public demo: replies stay on this screen and are never sent to the server.
    if (isDemoVisit()) {
      const demoPostId = circleThreadPost.id;
      setCircleThreadReplies(prev => [...prev, {
        id: -Date.now(),
        postId: demoPostId,
        text: circleThreadReplyText.trim(),
        createdAt: new Date().toISOString(),
        handle: circleProfile?.nickname ?? 'you',
        avatarEmoji: (() => { try { return sessionStorage.getItem('cw_demo_circle_emoji'); } catch { return null; } })() || '🌱',
        isMod: false,
      }]);
      const shownCount = circleThreadReplies.length + 1;
      setCircleThreadReplyCounts(prev => ({ ...prev, [demoPostId]: shownCount }));
      setCircleThreadReplyText('');
      toast('Demo: your reply is only visible to you and is not sent anywhere.');
      return;
    }
    if (!authToken) return;
    const postId = circleThreadPost.id;
    setCircleThreadReplyLoading(true);
    setCircleThreadReplyError(null);
    try {
      const res = await fetchWithRetry(`${API_BASE}/circle/posts/${postId}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        credentials: 'include',
        body: JSON.stringify({ text: circleThreadReplyText.trim() }),
      });
      if (res.status === 422) {
        const err = await res.json() as { error?: string };
        setCircleThreadReplyError(err.error || "Your reply couldn't be posted. The server flagged something — try rephrasing or check the community guidelines.");
        return;
      }
      if (!res.ok) { setCircleThreadReplyError("Your reply didn't go through. Try again."); return; }
      const data = await res.json() as CircleReply & { nudge?: string };
      setCircleThreadReplies(prev => [...prev, data]);
      setCircleThreadReplyCounts(prev => ({ ...prev, [postId]: (prev[postId] ?? 0) + 1 }));
      setCircleThreadReplyText('');
      if (data.nudge) setCircleThreadReplyError(data.nudge);
    } catch { /* request failed */ setCircleThreadReplyError("Your reply didn't go through. Try again."); }
    finally { setCircleThreadReplyLoading(false); }
  }

  async function loadCircleReactions() {
    if (!authToken) return;
    try {
      const res = await fetchWithRetry(`${API_BASE}/circle/reactions`, {
        headers: { Authorization: `Bearer ${authToken}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json() as { reactions: Record<number, Record<string, number>>; reacted: Record<string, boolean> };
        if (data.reactions) {
          setPeerReactions(prev => {
            const merged: Record<number, Record<string, number>> = { ...prev };
            for (const [postId, emojis] of Object.entries(data.reactions)) {
              merged[Number(postId)] = emojis;
            }
            return merged;
          });
        }
        if (data.reacted) setPeerReacted(data.reacted);
      }
    } catch { /* error handled silently */ }
  }

  function circleTimeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return `${Math.floor(d / 7)}w ago`;
  }

  return {
    // Circle identity / profile
    circleProfile, setCircleProfile,
    circleDisclosure, setCircleDisclosure,
    circleSetupModal, setCircleSetupModal,
    showCircleWelcome, setShowCircleWelcome,
    circleSetupNickname, setCircleSetupNickname,
    circleSetupBio, setCircleSetupBio,
    circleSetupNicknameError, setCircleSetupNicknameError,
    circleSetupBioError, setCircleSetupBioError,
    circleSetupSaving, setCircleSetupSaving,
    circleEditNickname, setCircleEditNickname,
    circleEditBio, setCircleEditBio,
    circleEditNicknameError, setCircleEditNicknameError,
    circleEditBioError, setCircleEditBioError,
    circleEditSaving, setCircleEditSaving,
    circleEditSaved, setCircleEditSaved,
    circleDisclosureEditAgeBracket, setCircleDisclosureEditAgeBracket,
    circleDisclosureEditScdType, setCircleDisclosureEditScdType,
    circleDisclosureEditScdOther, setCircleDisclosureEditScdOther,
    circleDisclosureEditScdShowOther, setCircleDisclosureEditScdShowOther,
    circleDisclosureEditTags, setCircleDisclosureEditTags,
    circleDisclosureEditTagInput, setCircleDisclosureEditTagInput,
    circleDisclosureEditTagError, setCircleDisclosureEditTagError,
    circleDisclosureEditSaving, setCircleDisclosureEditSaving,
    circleDisclosureEditSaved, setCircleDisclosureEditSaved,

    // Circle thread
    circleLivePosts, setCircleLivePosts,
    circleAbortRef,
    circleThreadPost, setCircleThreadPost,
    circleThreadReplies, setCircleThreadReplies,
    circleThreadLoading, setCircleThreadLoading,
    circleThreadReplyText, setCircleThreadReplyText,
    circleThreadReplyLoading, setCircleThreadReplyLoading,
    circleThreadReplyError, setCircleThreadReplyError,
    circleThreadReplyCounts, setCircleThreadReplyCounts,
    circleThreadRemovedReplies, setCircleThreadRemovedReplies,
    circleThreadReportedReplies, setCircleThreadReportedReplies,

    // Peer Connect
    peerFilter, setPeerFilter,
    peerAgeFilter, setPeerAgeFilter,
    peerScdFilter, setPeerScdFilter,
    peerView, setPeerView,
    peerSelectedTopic, setPeerSelectedTopic,
    peerReactions, setPeerReactions,
    peerReacted, setPeerReacted,
    peerEmojiPickerPost, setPeerEmojiPickerPost,
    peerPostModal, setPeerPostModal,
    peerPostType, setPeerPostType,
    peerPostText, setPeerPostText,
    peerRemovedPosts, setPeerRemovedPosts,
    peerRemovalModal, setPeerRemovalModal,
    peerRemovalReason, setPeerRemovalReason,
    peerRemovalConfirmed, setPeerRemovalConfirmed,
    peerPostError, setPeerPostError,
    peerPostNudge, setPeerPostNudge,
    peerPostLoading, setPeerPostLoading,
    showCircleEmojiPicker, setShowCircleEmojiPicker,
    circleEmojiDraft, setCircleEmojiDraft,
    peerRevealedPosts, setPeerRevealedPosts,

    // Safety
    safetyMutedWords, setSafetyMutedWords,
    safetyMutedMembers, setSafetyMutedMembers,
    safetyBlockedMembers, setSafetyBlockedMembers,
    safetyLoaded, setSafetyLoaded,
    safetyNewWord, setSafetyNewWord,
    safetyWordError, setSafetyWordError,

    // Media consent
    circleMediaConsentModal, setCircleMediaConsentModal,
    circleMediaConsentPendingAction, setCircleMediaConsentPendingAction,
    peerPostImage, setPeerPostImage,
    peerPostImageName, setPeerPostImageName,
    peerPostAltText, setPeerPostAltText,
    peerPostGif, setPeerPostGif,
    peerPostImageUploading, setPeerPostImageUploading,
    peerPostImageError, setPeerPostImageError,
    peerPostImageContentNote, setPeerPostImageContentNote,
    showGifPicker, setShowGifPicker,
    gifCategories, setGifCategories,
    gifItems, setGifItems,
    gifFilter, setGifFilter,
    adminGifCategories, setAdminGifCategories,
    adminModerationQueue, setAdminModerationQueue,

    // Functions
    loadSafetySettings,
    addMutedWord,
    removeMutedWord,
    muteMember,
    unmuteMember,
    blockMember,
    unblockMember,
    loadCirclePosts,
    openCircleThread,
    submitCircleReply,
    loadCircleReactions,
    circleTimeAgo,
  };
}
